// glonet-react-tailwind-project--src--components--AffiliateApplyDialog.js
// The /affiliates "apply for a founding slot" window — voice-first, exactly
// like the /hosts request flow (HostRequestDialog is the parent pattern):
// the agent starts IMMEDIATELY, asks the one application question, and writes
// the answer onto the visible application card through the set_application
// client tool. The member can edit the card by hand at any time (the agent
// gets a silent contextual note) and a typed path stays behind a link.
// Submission is submit_application (voice) or the Send button (typed).
import React, { useEffect, useRef, useState } from "react";
import { Conversation } from "@elevenlabs/client";
import api from "../services/api";
import { GLONET_AFFILIATE_INTAKE_AGENT_ID, GLONET_AI_EVENT_ID } from "../config/glonetai";
import MicSelector, { GA_MIC_DEVICE_KEY } from "./MicSelector";
import log from "../utils/logger";
import {
  gvTel, gvTelReset, gvMark, gvTimeSpan, gvTimedTools, gvModeRecorder,
} from "../utils/voiceTelemetry";

const ACCENT = "#74a0f5";
const MONO = "'JetBrains Mono',monospace";
const SERIF = "'Instrument Serif',serif";

export default function AffiliateApplyDialog({ onClose, micSilenceSecs = 5 }) {
  const [phase, setPhase] = useState("starting"); // starting | live | typing | sending | done | already | error
  const [errMsg, setErrMsg] = useState("");
  const [text, setText] = useState("");
  const [code, setCode] = useState(null);
  const [speaking, setSpeaking] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [heardTick, setHeardTick] = useState(0);
  const sessionRef = useRef(null);
  const phaseRef = useRef("starting"); phaseRef.current = phase;
  const textRef = useRef(""); textRef.current = text;
  const fromToolRef = useRef(false);  // last card change came from set_application
  // Reach as NUMBERS (2026-08-09). The card sentence reads well and computes
  // nothing; these let us answer "which affiliates are most of our reach".
  // Optional by design — never block an application on a figure.
  const reachRef = useRef({ audience_size: null, expected_joiners: null });
  const liveTextRef = useRef(null);   // what the agent last knows the card says

  const endVoice = async () => {
    const s = sessionRef.current;
    sessionRef.current = null;
    if (s) { try { await s.endSession(); } catch (e) { /* already closed */ } }
  };

  const submit = async (source) => {
    if (phaseRef.current === "sending" || phaseRef.current === "done") return;
    setPhase("sending");
    try {
      const r = reachRef.current;
      const { data } = await api.post("affiliates/apply", {
        event_id: GLONET_AI_EVENT_ID,
        message: (textRef.current || "").trim().slice(0, 2000),
        ...(r.audience_size != null ? { audience_size: r.audience_size } : {}),
        ...(r.expected_joiners != null ? { expected_joiners: r.expected_joiners } : {}),
        ...(r.audience_size != null || r.expected_joiners != null
            ? { reach_source: source === "voice" ? "voice" : "typed" } : {}),
      });
      if (data.status === "approved" && data.affiliate_code) {
        setCode(data.affiliate_code);
        setPhase("already");
      } else {
        setPhase("done");
      }
      await endVoice();
    } catch (e) {
      log.error("affiliate apply failed", { error: e, source });
      setErrMsg("Couldn't submit — try again in a moment.");
      setPhase("error");
    }
  };

  // Voice starts as soon as the window opens — this IS the application flow.
  useEffect(() => {
    let cancelled = false;
    gvTelReset("affiliate intake");
    (async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        gvMark("mic_granted");
        // Bluetooth headsets swap profiles when the mic engages and swallow
        // the greeting without this pause (same fix as VoiceOnboarding).
        await new Promise((r) => setTimeout(r, 1200));
        if (cancelled) return;
        let mic = localStorage.getItem(GA_MIC_DEVICE_KEY) || "";
        if (mic) {
          try {
            const devs = await navigator.mediaDevices.enumerateDevices();
            if (!devs.some((d) => d.kind === "audioinput" && d.deviceId === mic)) {
              localStorage.removeItem(GA_MIC_DEVICE_KEY);
              mic = "";
            }
          } catch (e) { /* enumeration unavailable — try it anyway */ }
        }
        const recordMode = gvModeRecorder("affiliate-intake");
        const session = await gvTimeSpan("session_connect", () => Conversation.startSession({
          agentId: GLONET_AFFILIATE_INTAKE_AGENT_ID,
          connectionType: "websocket",
          ...(mic ? { inputDeviceId: mic } : {}),
          dynamicVariables: {
            first_line: ("Happy to take your founding affiliate application. One question "
              + "covers it: who are your people, and how do you reach them?"),
          },
          clientTools: gvTimedTools("affiliate-intake", {
            set_application: async (args) => {
              const t = ((args && args.text) || "").slice(0, 2000);
              if (t) { fromToolRef.current = true; setText(t); }
              // The agent omits these when the member never said a number —
              // keep whatever we already had rather than blanking it.
              const num = (v) => {
                const n = typeof v === "string" ? parseInt(v, 10) : v;
                return Number.isFinite(n) && n >= 0 && n <= 1e9 ? Math.round(n) : null;
              };
              const a = num(args && args.audience_size);
              const e = num(args && args.expected_joiners);
              if (a != null) reachRef.current.audience_size = a;
              if (e != null) reachRef.current.expected_joiners = e;
            },
            submit_application: async () => { await submit("voice"); },
          }),
          onModeChange: ({ mode }) => {
            recordMode(mode);
            setSpeaking(mode === "speaking");
            if (mode === "speaking") setThinking(false);
          },
          onMessage: ({ source }) => {
            gvTel("message", { surface: "affiliate-intake", source });
            if (source === "user") { setThinking(true); setHeardTick(Date.now()); }
            else setThinking(false);
          },
          onDisconnect: () => {
            sessionRef.current = null;
            setPhase((prev) => (prev === "live" || prev === "starting" ? "typing" : prev));
          },
        }));
        if (cancelled) { try { await session.endSession(); } catch (e) { /* closing */ } return; }
        sessionRef.current = session;
        gvMark("session_live");
        setPhase("live");
      } catch (e) {
        log.error("affiliate intake voice failed", { error: e });
        if (!cancelled) {
          setErrMsg("Microphone unavailable — type it below instead.");
          setPhase("typing");
        }
      }
    })();
    return () => { cancelled = true; endVoice(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hand edits to the card mid-conversation reach the agent silently;
  // set_application changes skip it (the agent wrote them).
  useEffect(() => {
    if (phase !== "live") return undefined;
    if (liveTextRef.current === null || fromToolRef.current) {
      fromToolRef.current = false;
      liveTextRef.current = text;
      return undefined;
    }
    if (text === liveTextRef.current) return undefined;
    const t = setTimeout(() => {
      liveTextRef.current = text;
      const s = sessionRef.current;
      if (s && typeof s.sendContextualUpdate === "function") {
        try {
          s.sendContextualUpdate(
            `The member hand-edited the application card. It now reads: "${text}". `
            + "This text is authoritative — acknowledge the change in a few words at your "
            + "next turn; do not re-read the card.");
        } catch (e) { /* socket closing */ }
      }
    }, 900);
    return () => clearTimeout(t);
  }, [text, phase]);

  const changeMic = async (deviceId) => {
    const s = sessionRef.current;
    if (s && typeof s.changeInputDevice === "function") {
      try {
        await s.changeInputDevice(deviceId ? { inputDeviceId: deviceId } : {});
      } catch (e) { log.error("mic switch failed", { error: e }); }
    }
  };

  const switchToTyping = async () => { await endVoice(); setPhase("typing"); };

  const statusText =
    phase === "starting" ? "Connecting…"
      : speaking ? "Agent speaking — jump in any time"
      : thinking ? "Thinking…" : "Listening…";

  const primaryBtn = { fontSize: "14px", fontWeight: 600, color: "#0c0d10", background: ACCENT,
    cursor: "pointer", border: "none", padding: "12px 24px", borderRadius: "999px" };

  return (
    <div onClick={onClose}
         style={{ position: "fixed", inset: 0, zIndex: 80, background: "rgba(5,6,9,0.88)",
           backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
           display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: "#12141c", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "18px",
             padding: "30px 32px", maxWidth: "560px", width: "100%",
             boxShadow: "0 24px 70px -14px rgba(0,0,0,0.9)", color: "#f1f2f4" }}>
        {(phase === "done" || phase === "already") ? (
          <div style={{ textAlign: "center", padding: "18px 0" }}>
            <div style={{ fontSize: "34px", marginBottom: "14px" }}>✓</div>
            <div style={{ fontFamily: SERIF, fontSize: "24px", marginBottom: "8px" }}>
              {phase === "already" ? "You already have a link." : "Application in."}
            </div>
            <div style={{ fontSize: "14.5px", lineHeight: 1.6, color: "#b6b9c0", marginBottom: "22px" }}>
              {phase === "already" ? (
                <>Your code is <span style={{ fontFamily: MONO }}>{code}</span>. Finish payout setup at{" "}
                  <a href={`/affiliate-onboarding/${code}`} style={{ color: ACCENT }}>affiliate onboarding</a>.</>
              ) : ("We review daily. Approval creates your link and emails your Stripe payout setup. Watch your inbox.")}
            </div>
            <button onClick={onClose} style={primaryBtn}>Done</button>
          </div>
        ) : (
          <>
            <div style={{ fontFamily: MONO, fontSize: "11px", letterSpacing: "0.22em", color: ACCENT,
              textTransform: "uppercase", marginBottom: "10px" }}>
              Apply for a founding slot
            </div>

            {/* the application card — the agent writes here; edit any time */}
            <div style={{ marginBottom: "14px", padding: "12px 14px", borderRadius: "10px",
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <div style={{ fontFamily: MONO, fontSize: "10px", letterSpacing: "0.18em", color: "#6b6f78",
                marginBottom: "8px" }}>YOUR APPLICATION — EDIT ANY TIME</div>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
                        placeholder={phase === "live" || phase === "starting"
                          ? "Answer out loud — your words land here as you speak…"
                          : "Who are your people, roughly how many, and how do you reach them?"}
                        style={{ width: "100%", resize: "vertical", fontSize: "14px", lineHeight: 1.55,
                          color: "#f1f2f4", background: "#0c0d10",
                          border: "1px solid rgba(255,255,255,0.14)", borderRadius: "10px",
                          padding: "10px 12px", outline: "none", boxSizing: "border-box" }} />
            </div>

            {(phase === "starting" || phase === "live") && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <span style={{ flex: "none", width: 40, height: 40, borderRadius: "50%",
                    background: phase === "live" ? ACCENT : "rgba(255,255,255,0.08)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    animation: phase === "live" ? "al-breathe-mic 1.8s infinite" : "none" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                         stroke={phase === "live" ? "#0c0d10" : "#f1f2f4"} strokeWidth="1.8" strokeLinecap="round">
                      <rect x="9" y="3" width="6" height="11" rx="3" />
                      <path d="M5 11 a7 7 0 0 0 14 0" />
                      <line x1="12" y1="18" x2="12" y2="21" />
                    </svg>
                  </span>
                  <div style={{ fontSize: "14px", color: "#dfe1e5" }}>{statusText}</div>
                </div>
                <style>{`@keyframes al-breathe-mic { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.08); opacity: .82; } }`}</style>
                <div style={{ fontSize: "12.5px", lineHeight: 1.6, color: "#7c818b", marginTop: "10px" }}>
                  The agent asks the one question, writes your answer onto the card
                  above, reads it back, and submits when you say so. It can also
                  answer questions about the founding terms.
                </div>
                <MicSelector live={phase === "live"} silenceSecs={micSilenceSecs}
                             onDeviceChange={changeMic} holdOff={speaking || thinking} heardTs={heardTick} />
              </>
            )}

            {errMsg && <div style={{ fontSize: "13px", color: "#e88", marginTop: "10px" }}>{errMsg}</div>}

            <div style={{ display: "flex", gap: "12px", alignItems: "center", marginTop: "18px" }}>
              {(phase === "starting" || phase === "live") && (
                <button onClick={switchToTyping}
                        style={{ fontSize: "12.5px", color: "#7c818b", background: "none", border: "none",
                          cursor: "pointer", textDecoration: "underline", padding: 0 }}>
                  prefer to type?
                </button>
              )}
              <div style={{ marginLeft: "auto", display: "flex", gap: "12px" }}>
                <button onClick={onClose}
                        style={{ fontSize: "14px", color: "#b6b9c0", background: "transparent", cursor: "pointer",
                          border: "1px solid rgba(255,255,255,0.18)", padding: "11px 20px", borderRadius: "999px" }}>
                  Cancel
                </button>
                {(phase === "typing" || phase === "error" || phase === "sending") && (
                  <button onClick={() => submit("text")}
                          disabled={phase === "sending" || !text.trim()}
                          style={{ ...primaryBtn, padding: "11px 24px",
                            opacity: phase === "sending" || !text.trim() ? 0.55 : 1 }}>
                    {phase === "sending" ? "Sending…" : "Submit application"}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
